import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Info, Lock, Plus, Search, Trash2, X } from "lucide-react";

import ContentLoader from "../../components/ContentLoader";
import SubCapabilityQuestionListEditor from "../../components/SubCapabilityQuestionListEditor";
import { Alert, Badge, Button } from "../../components/ui";
import {
  normalizeAssessmentFieldsFromApi,
  withAddedMaturityOption,
  withCustomMaturityToggle,
  withMaturityOptionPatch,
  withRemovedMaturityOption,
} from "../../utils/subCapabilityAssessmentConfig";
import { dashboardToast } from "../../utils/dashboardToast";
import { getCompanyList } from "../../services/companyApi";
import { getProjectList } from "../../services/projectApi";
import { getTraxccelFrameworks } from "../../services/traxccelFrameworkApi";
import {
  createAssessmentBlueprintFromFramework,
  getAssessmentBlueprint,
  publishAssessmentBlueprint,
  saveAssessmentBlueprint,
} from "../../services/assessmentBlueprintApi";
import type {
  AssessmentBlueprintCapability,
  AssessmentBlueprintDetail,
  AssessmentBlueprintQuestion,
} from "../../types/assessmentBlueprint";
import {
  WIZARD_STEPS,
  blueprintUnitName,
  buildSavePayload,
  countIncludedQuestions,
  resolveBlueprintEditTarget,
  updateBlueprintUnit,
  type BlueprintAssessmentUnit,
  type WizardStepKey,
} from "../../utils/assessmentBlueprintUtils";
import { QUESTION_STATE } from "../../utils/subCapabilityQuestionUtils";
import { FRAMEWORK_STUDIO_LABELS } from "../../utils/frameworkStudioLabels";

const toEditorQuestions = (questions: AssessmentBlueprintQuestion[]) =>
  questions.map((q) => ({
    id: q.id,
    questionText: q.questionText,
    stateKind: q.stateKind,
    displayOrder: q.displayOrder,
    isActive: q.isActive,
  }));

const fromEditorQuestions = (
  existing: AssessmentBlueprintQuestion[],
  stateKind: typeof QUESTION_STATE.current | typeof QUESTION_STATE.future,
  edited: ReturnType<typeof toEditorQuestions>
): AssessmentBlueprintQuestion[] =>
  edited.map((row, index) => {
    const match = existing.find((q) => q.id === row.id && q.id > 0);
    return {
      id: row.id ?? 0,
      sourceQuestionId: match?.sourceQuestionId ?? null,
      blueprintCapabilityId: match?.blueprintCapabilityId ?? null,
      blueprintSubCapabilityId: match?.blueprintSubCapabilityId ?? null,
      questionText: row.questionText,
      stateKind,
      displayOrder: index,
      isIncluded: true,
      isActive: row.isActive !== false,
    };
  });

type SearchSelectProps = {
  id: string;
  label: string;
  hint?: string;
  placeholder: string;
  disabled?: boolean;
  options: Array<{ id: number; name: string }>;
  selectedId: number | null;
  selectedName: string;
  onSelect: (id: number | null) => void;
};

/** Searchable single-select (replaces the native select in the company / project steps). */
const SearchSelect: React.FC<SearchSelectProps> = ({
  id,
  label,
  hint,
  placeholder,
  disabled,
  options,
  selectedId,
  selectedName,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  }, [options, query]);

  return (
    <div className="app-field">
      <label htmlFor={id} className="app-field__label">
        {label}
      </label>
      {hint ? <p className="ab-wizard__hint" style={{ marginTop: "-0.25rem" }}>{hint}</p> : null}
      <div className="ab-wizard__combo">
        <div className={open ? "ab-wizard__search ab-wizard__search--open" : "ab-wizard__search"}>
          <Search size={16} aria-hidden />
          <input
            id={id}
            className="app-input"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${id}-list`}
            autoComplete="off"
            placeholder={placeholder}
            disabled={disabled}
            value={open ? query : selectedName}
            onFocus={() => {
              setQuery("");
              setOpen(true);
            }}
            onBlur={() => setOpen(false)}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setOpen(false);
            }}
          />
          {selectedId != null && !disabled ? (
            <button
              type="button"
              className="ab-wizard__search-clear"
              aria-label={`Clear ${label.toLowerCase()}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onSelect(null)}
            >
              <X size={16} aria-hidden />
            </button>
          ) : null}
        </div>
        {open ? (
          <ul id={`${id}-list`} className="ab-wizard__listbox" role="listbox">
            {matches.length === 0 ? (
              <li className="ab-wizard__listbox-empty">No results</li>
            ) : (
              matches.map((option) => (
                <li key={option.id} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.id === selectedId}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onSelect(option.id);
                      setOpen(false);
                    }}
                  >
                    {option.name}
                  </button>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export type AssessmentBlueprintWizardPanelProps = {
  /** null = create new from framework */
  blueprintId: number | null;
  onClose: () => void;
  onCompleted: () => void;
  /** Fired whenever a draft is created or saved, so the host list can refresh even if the modal is just closed. */
  onSaved?: () => void;
  /** Lets the host modal show the draft title under its heading. */
  onSubtitleChange?: (subtitle: string) => void;
};

export const AssessmentBlueprintWizardPanel: React.FC<AssessmentBlueprintWizardPanelProps> = ({
  blueprintId,
  onClose,
  onCompleted,
  onSaved,
  onSubtitleChange,
}) => {
  const isNew = blueprintId == null;
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [blueprint, setBlueprint] = useState<AssessmentBlueprintDetail | null>(null);

  const [frameworkOptions, setFrameworkOptions] = useState<
    Array<{ id: number; title: string; description?: string; capabilityCount?: number }>
  >([]);
  const [frameworkSearch, setFrameworkSearch] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [unitTab, setUnitTab] = useState<"questions" | "maturity">("questions");
  const [frameworksLoading, setFrameworksLoading] = useState(false);
  const [selectedFrameworkId, setSelectedFrameworkId] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [companyOptions, setCompanyOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [projectOptions, setProjectOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [activeCapId, setActiveCapId] = useState<number | null>(null);
  const [activeSubId, setActiveSubId] = useState<number | null>(null);

  const currentStep = WIZARD_STEPS[stepIndex]?.key as WizardStepKey;
  const isReadOnly = blueprint?.status === "Published";

  useEffect(() => {
    onSubtitleChange?.(blueprint?.title || (isNew ? "New draft" : ""));
  }, [blueprint?.title, isNew, onSubtitleChange]);

  const seedActiveUnits = (detail: AssessmentBlueprintDetail) => {
    if (detail.capabilities.length > 0) {
      setActiveCapId(detail.capabilities[0].id);
      setActiveSubId(detail.capabilities[0].subCapabilities[0]?.id ?? null);
    }
  };

  const loadBlueprint = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const detail = await getAssessmentBlueprint(id);
        setBlueprint(detail);
        setStepIndex(detail.status === "Published" ? WIZARD_STEPS.length - 1 : 1);
        seedActiveUnits(detail);
      } catch (error) {
        dashboardToast.error(error instanceof Error ? error.message : "Failed to load assessment.");
        onClose();
      } finally {
        setLoading(false);
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (blueprintId != null && blueprintId > 0) {
      loadBlueprint(blueprintId);
      return;
    }
    setBlueprint(null);
    setStepIndex(0);
    setSelectedFrameworkId("");
    setDraftTitle("");
    setDraftDescription("");
    setLoading(false);
  }, [blueprintId, loadBlueprint]);

  useEffect(() => {
    if (currentStep !== "framework") return;
    setFrameworksLoading(true);
    getTraxccelFrameworks(1, 500, "name-asc", "")
      .then((result) =>
        setFrameworkOptions(
          (result.items || []).map((f) => ({
            id: f.id,
            title: f.title || `Framework ${f.id}`,
            description: f.description || undefined,
            capabilityCount:
              f.capabilityCount != null ? Number(f.capabilityCount) : undefined,
          }))
        )
      )
      .catch(() => setFrameworkOptions([]))
      .finally(() => setFrameworksLoading(false));
  }, [currentStep]);

  useEffect(() => {
    if (currentStep !== "company") return;
    getCompanyList(1, 500, "name-asc", "")
      .then((result) =>
        setCompanyOptions(
          (result.items || []).map((c) => ({
            id: c.id ?? c.companyId,
            name: c.companyName ?? c.name ?? `Company ${c.id}`,
          }))
        )
      )
      .catch(() => setCompanyOptions([]));
  }, [currentStep]);

  useEffect(() => {
    if (currentStep !== "project" || !blueprint?.companyId) {
      setProjectOptions([]);
      return;
    }
    getProjectList(blueprint.companyId)
      .then((projects) =>
        setProjectOptions(
          (projects || []).map((p) => ({
            id: p.id ?? p.projectId,
            name: p.projectName ?? p.name ?? `Project ${p.id}`,
          }))
        )
      )
      .catch(() => setProjectOptions([]));
  }, [currentStep, blueprint?.companyId]);

  const updateBlueprint = (patch: Partial<AssessmentBlueprintDetail>) => {
    setBlueprint((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateCapabilities = (next: AssessmentBlueprintCapability[]) => {
    updateBlueprint({ capabilities: next });
  };

  const persistDraft = async (nextBlueprint?: AssessmentBlueprintDetail) => {
    const payloadSource = nextBlueprint ?? blueprint;
    if (!payloadSource) return null;
    setSaving(true);
    try {
      const saved = await saveAssessmentBlueprint(buildSavePayload(payloadSource));
      setBlueprint(saved);
      onSaved?.();
      return saved;
    } catch (error) {
      dashboardToast.error(error instanceof Error ? error.message : "Save failed.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFromFramework = async () => {
    const frameworkId = Number(selectedFrameworkId);
    if (!frameworkId) {
      dashboardToast.error("Select a Traxccel Framework.");
      return;
    }
    if (!draftTitle.trim()) {
      setTitleTouched(true);
      dashboardToast.error("Enter a title for this assessment.");
      return;
    }
    setSaving(true);
    try {
      const created = await createAssessmentBlueprintFromFramework({
        traxccelFrameworkId: frameworkId,
        title: draftTitle.trim() || undefined,
        description: draftDescription.trim() || undefined,
      });
      setBlueprint(created);
      onSaved?.();
      setStepIndex(1);
      seedActiveUnits(created);
      dashboardToast.success("Assessment created from assessment framework.");
    } catch (error) {
      dashboardToast.error(error instanceof Error ? error.message : "Create failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === "framework") {
      if (blueprint) {
        setStepIndex(1);
        return;
      }
      await handleCreateFromFramework();
      return;
    }

    if (blueprint && !isReadOnly) {
      const saved = await persistDraft();
      if (!saved) return;
    }

    if (stepIndex < WIZARD_STEPS.length - 1) {
      setStepIndex((prev) => prev + 1);
    }
  };

  const handlePublish = async () => {
    if (!blueprint) return;
    setSaving(true);
    try {
      const saved = await persistDraft();
      if (!saved) return;
      await publishAssessmentBlueprint(saved.id);
      dashboardToast.success("Assessment published.");
      onCompleted();
    } catch (error) {
      dashboardToast.error(error instanceof Error ? error.message : "Publish failed.");
    } finally {
      setSaving(false);
    }
  };

  const activeCapability = useMemo(
    () =>
      blueprint?.capabilities.find((c) => c.id === activeCapId) ??
      blueprint?.capabilities[0] ??
      null,
    [blueprint, activeCapId]
  );

  const activeSub = useMemo(() => {
    if (!activeCapability) return null;
    return (
      activeCapability.subCapabilities.find((s) => s.id === activeSubId) ??
      activeCapability.subCapabilities[0] ??
      null
    );
  }, [activeCapability, activeSubId]);

  const editTarget = useMemo(
    () => resolveBlueprintEditTarget(activeCapability, activeSubId),
    [activeCapability, activeSubId]
  );

  const patchEditTarget = (patch: Partial<BlueprintAssessmentUnit>) => {
    if (!blueprint || !editTarget) return;
    updateCapabilities(
      updateBlueprintUnit(
        blueprint.capabilities,
        editTarget.capabilityId,
        editTarget.subId,
        patch
      )
    );
  };

  const updateUnitQuestions = (
    stateKind: typeof QUESTION_STATE.current | typeof QUESTION_STATE.future,
    edited: ReturnType<typeof toEditorQuestions>
  ) => {
    if (!editTarget) return;
    const unit = editTarget.unit;
    const questionKey =
      stateKind === QUESTION_STATE.current ? "currentStateQuestions" : "futureStateQuestions";
    patchEditTarget({
      [questionKey]: fromEditorQuestions(unit[questionKey], stateKind, edited),
    });
  };

  const unitHasCustomScale = (unit: BlueprintAssessmentUnit) =>
    Boolean(normalizeAssessmentFieldsFromApi({ maturityOptionsJson: unit.maturityOptionsJson }).useCustomMaturity);

  const customScaleUnitNames = useMemo(() => {
    if (!blueprint) return [] as string[];
    const names: string[] = [];
    for (const cap of blueprint.capabilities) {
      const units: BlueprintAssessmentUnit[] = cap.subCapabilities.length
        ? cap.subCapabilities
        : [cap];
      for (const unit of units) {
        if (unitHasCustomScale(unit)) names.push(blueprintUnitName(unit));
      }
    }
    return names;
  }, [blueprint]);

  const companyName = blueprint
    ? companyOptions.find((c) => c.id === blueprint.companyId)?.name || blueprint.companyName || ""
    : "";
  const projectName = blueprint
    ? projectOptions.find((p) => p.id === blueprint.projectId)?.name || blueprint.projectName || ""
    : "";

  const filteredFrameworks = useMemo(() => {
    const q = frameworkSearch.trim().toLowerCase();
    if (!q) return frameworkOptions;
    return frameworkOptions.filter((f) => f.title.toLowerCase().includes(q));
  }, [frameworkOptions, frameworkSearch]);

  const selectedFramework = useMemo(
    () => frameworkOptions.find((f) => String(f.id) === selectedFrameworkId),
    [frameworkOptions, selectedFrameworkId]
  );

  const renderSoFar = () => {
    if (!blueprint) return null;
    const customLabel = customScaleUnitNames.length
      ? `Framework default · ${
          customScaleUnitNames.length === 1
            ? `${customScaleUnitNames[0]} custom`
            : `${customScaleUnitNames.length} custom`
        }`
      : "Framework default";
    return (
      <aside className="ab-wizard__sofar" aria-label="Assessment summary so far">
        <p className="ab-wizard__eyebrow">So far</p>
        <dl>
          <div>
            <dt>Title</dt>
            <dd>{blueprint.title}</dd>
          </div>
          <div>
            <dt>Framework</dt>
            <dd>{blueprint.sourceTraxccelFrameworkTitle}</dd>
          </div>
          <div>
            <dt>Questions</dt>
            <dd>
              {countIncludedQuestions(blueprint)} across {blueprint.capabilities.length}{" "}
              {blueprint.capabilities.length === 1 ? "capability" : "capabilities"}
            </dd>
          </div>
          <div>
            <dt>Maturity scale</dt>
            <dd>{customLabel}</dd>
          </div>
          {currentStep === "project" && companyName ? (
            <div>
              <dt>Company</dt>
              <dd>{companyName}</dd>
            </div>
          ) : null}
        </dl>
      </aside>
    );
  };

  const renderMaturityTab = () => {
    if (!editTarget) return null;
    const fields = normalizeAssessmentFieldsFromApi({
      maturityOptionsJson: editTarget.unit.maturityOptionsJson,
    });
    const options = fields.maturityOptions ?? [];
    const isCustom = Boolean(fields.useCustomMaturity);
    const save = (next: typeof fields) =>
      patchEditTarget({ maturityOptionsJson: next.maturityOptionsJson ?? null });

    return (
      <div>
        <div className="ab-wizard__scale-bar">
          <div className="ab-wizard__segmented" role="group" aria-label="Maturity scale source">
            <button
              type="button"
              aria-pressed={!isCustom}
              disabled={isReadOnly}
              onClick={() => isCustom && save(withCustomMaturityToggle(fields, false))}
            >
              Framework default
            </button>
            <button
              type="button"
              aria-pressed={isCustom}
              disabled={isReadOnly}
              onClick={() => !isCustom && save(withCustomMaturityToggle(fields, true))}
            >
              Custom
            </button>
          </div>
          <span className="ab-wizard__hint">Applies to every question in this capability.</span>
        </div>

        <div className="ab-wizard__scale-table">
          <div className="ab-wizard__scale-row ab-wizard__scale-row--head">
            <span className="ab-wizard__eyebrow ab-wizard__scale-lvl">Lvl</span>
            <span className="ab-wizard__eyebrow">Label</span>
            <span className="ab-wizard__eyebrow">Definition</span>
            <span />
          </div>
          {options.map((option, index) =>
            isCustom ? (
              <div key={`${option.optionValue}-${index}`} className="ab-wizard__scale-row">
                <span className="ab-wizard__scale-lvl">{index + 1}</span>
                <input
                  className="app-input"
                  aria-label={`Level ${index + 1} label`}
                  value={option.label || ""}
                  disabled={isReadOnly}
                  onChange={(e) =>
                    save(withMaturityOptionPatch(fields, index, { label: e.target.value }))
                  }
                />
                <textarea
                  className="app-input"
                  rows={2}
                  aria-label={`Level ${index + 1} definition`}
                  value={option.definition || ""}
                  disabled={isReadOnly}
                  onChange={(e) =>
                    save(withMaturityOptionPatch(fields, index, { definition: e.target.value }))
                  }
                />
                <button
                  type="button"
                  className="ab-wizard__icon-btn"
                  aria-label={`Remove level ${index + 1}`}
                  disabled={isReadOnly || options.length <= 1}
                  onClick={() => save(withRemovedMaturityOption(fields, index))}
                >
                  <Trash2 size={16} aria-hidden />
                </button>
              </div>
            ) : (
              <div
                key={`${option.optionValue}-${index}`}
                className="ab-wizard__scale-row ab-wizard__scale-row--static"
              >
                <span className="ab-wizard__scale-lvl">{index + 1}</span>
                <span className="ab-wizard__scale-label">{option.label}</span>
                <span className="ab-wizard__scale-def">{option.definition}</span>
                <span />
              </div>
            )
          )}
        </div>
        {isCustom ? (
          <button
            type="button"
            className="ab-wizard__add-btn"
            disabled={isReadOnly}
            onClick={() => save(withAddedMaturityOption(fields))}
          >
            <Plus size={14} aria-hidden />
            Add level
          </button>
        ) : null}
      </div>
    );
  };

  const renderStepContent = () => {
    if (loading) return <ContentLoader label="Loading…" />;

    if (currentStep === "framework") {
      if (blueprint) {
        return (
          <div className="ab-wizard__pad ab-wizard__stack">
            <div>
              <h2 className="ab-wizard__step-title">Framework</h2>
              <p className="ab-wizard__step-desc">
                The framework is copied into this assessment as a snapshot.
              </p>
            </div>
            <Alert
              variant="info"
              description={`Using ${blueprint.sourceTraxccelFrameworkTitle}. Continue to edit questions.`}
            />
          </div>
        );
      }
      return (
        <div className="ab-wizard__split ab-wizard__split--details">
          <div>
            <h2 className="ab-wizard__step-title">
              Choose a framework<span className="app-field__required">*</span>
            </h2>
            <p className="ab-wizard__step-desc">
              It&apos;s copied into this assessment as a snapshot, so later framework edits
              won&apos;t affect it.
            </p>
            <div className="ab-wizard__search" style={{ marginTop: "0.9rem" }}>
              <Search size={16} aria-hidden />
              <input
                id="ab-wizard-framework-search"
                className="app-input"
                type="search"
                placeholder="Search frameworks…"
                aria-label="Search frameworks"
                value={frameworkSearch}
                onChange={(event) => setFrameworkSearch(event.target.value)}
                disabled={saving}
              />
            </div>
            <div
              className="ab-wizard__radio-list"
              role="radiogroup"
              aria-label="Framework"
              aria-busy={frameworksLoading}
            >
              {frameworksLoading ? (
                <p className="ab-wizard__hint">Loading assessment frameworks…</p>
              ) : filteredFrameworks.length === 0 ? (
                <p className="ab-wizard__hint">No frameworks found.</p>
              ) : (
                filteredFrameworks.map((framework) => {
                  const selected = selectedFrameworkId === String(framework.id);
                  return (
                    <label
                      key={framework.id}
                      className={
                        selected
                          ? "ab-wizard__radio-card ab-wizard__radio-card--selected"
                          : "ab-wizard__radio-card"
                      }
                    >
                      <input
                        type="radio"
                        name="ab-wizard-framework"
                        checked={selected}
                        disabled={saving}
                        onChange={() => setSelectedFrameworkId(String(framework.id))}
                      />
                      <span className="ab-wizard__radio-card-body">
                        <span className="ab-wizard__radio-card-title">{framework.title}</span>
                        {framework.description ? (
                          <span className="ab-wizard__radio-card-sub">{framework.description}</span>
                        ) : null}
                      </span>
                      {framework.capabilityCount != null ? (
                        <span
                          className={
                            selected ? "ab-wizard__pill ab-wizard__pill--accent" : "ab-wizard__pill"
                          }
                        >
                          {framework.capabilityCount}{" "}
                          {framework.capabilityCount === 1 ? "capability" : "capabilities"}
                        </span>
                      ) : null}
                    </label>
                  );
                })
              )}
            </div>
          </div>
          <div className="ab-wizard__aside">
            <div className="ab-wizard__aside-card">
            <h3 className="ab-wizard__aside-title">Details</h3>
            <div className="ab-wizard__stack">
              <div className="app-field">
                <label htmlFor="ab-wizard-title" className="app-field__label">
                  Title<span className="app-field__required">*</span>
                </label>
                <input
                  id="ab-wizard-title"
                  className="app-input"
                  value={draftTitle}
                  onChange={(event) => setDraftTitle(event.target.value)}
                  onBlur={() => setTitleTouched(true)}
                  placeholder="Name this assessment"
                  required
                  aria-invalid={titleTouched && !draftTitle.trim()}
                  disabled={saving}
                />
                {titleTouched && !draftTitle.trim() ? (
                  <p className="ab-wizard__hint" style={{ color: "var(--app-danger, #d64545)" }}>
                    Title is required.
                  </p>
                ) : null}
              </div>
              <div className="app-field">
                <label htmlFor="ab-wizard-description" className="app-field__label">
                  Description <span className="ab-wizard__label-opt">optional</span>
                </label>
                <textarea
                  id="ab-wizard-description"
                  className="app-input"
                  rows={6}
                  value={draftDescription}
                  onChange={(event) => setDraftDescription(event.target.value)}
                  placeholder="Notes for this assessment"
                  disabled={saving}
                />
              </div>
            </div>
            </div>
            <div
              className={
                selectedFramework
                  ? "ab-wizard__aside-card ab-wizard__selected-fw ab-wizard__selected-fw--set"
                  : "ab-wizard__aside-card ab-wizard__selected-fw"
              }
            >
              <p className="ab-wizard__eyebrow">Selected framework</p>
              {selectedFramework ? (
                <>
                  <p className="ab-wizard__selected-fw-title">{selectedFramework.title}</p>
                  {selectedFramework.capabilityCount != null ? (
                    <p className="ab-wizard__selected-fw-meta">
                      {selectedFramework.capabilityCount}{" "}
                      {selectedFramework.capabilityCount === 1 ? "capability" : "capabilities"}
                    </p>
                  ) : null}
                  {selectedFramework.description ? (
                    <p className="ab-wizard__selected-fw-desc">{selectedFramework.description}</p>
                  ) : null}
                </>
              ) : (
                <p className="ab-wizard__hint">Pick a framework from the list to see it here.</p>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (!blueprint) return null;

    if (currentStep === "questions") {
      const unit = editTarget?.unit;
      const questionTotal = unit
        ? [...unit.currentStateQuestions, ...unit.futureStateQuestions].filter(
            (q) => q.isActive && q.questionText.trim()
          ).length
        : 0;
      return (
        <div className="ab-wizard__questions-layout">
          <aside className="ab-wizard__nav" aria-label="Capabilities">
            <div className="ab-wizard__nav-inner">
            <p className="ab-wizard__nav-heading">Capabilities</p>
            <p className="ab-wizard__eyebrow" style={{ marginBottom: "0.35rem" }}>
              {blueprint.sourceTraxccelFrameworkTitle}
            </p>
            {blueprint.capabilities.map((cap) => (
              <React.Fragment key={cap.id}>
                <button
                  type="button"
                  className={
                    activeCapId === cap.id
                      ? "ab-wizard__nav-item ab-wizard__nav-item--active"
                      : "ab-wizard__nav-item"
                  }
                  onClick={() => {
                    setActiveCapId(cap.id);
                    setActiveSubId(cap.subCapabilities[0]?.id ?? null);
                  }}
                >
                  <span>{cap.capabilityName}</span>
                  {cap.subCapabilities.length === 0 && unitHasCustomScale(cap) ? (
                    <span className="ab-wizard__pill ab-wizard__pill--accent">Custom scale</span>
                  ) : null}
                </button>
                {activeCapId === cap.id
                  ? cap.subCapabilities.map((sub) => (
                      <button
                        key={sub.id}
                        type="button"
                        className={
                          activeSubId === sub.id
                            ? "ab-wizard__nav-item ab-wizard__nav-item--sub ab-wizard__nav-item--active"
                            : "ab-wizard__nav-item ab-wizard__nav-item--sub"
                        }
                        onClick={() => setActiveSubId(sub.id)}
                      >
                        <span>{sub.subCapabilityName}</span>
                        {unitHasCustomScale(sub) ? (
                          <span className="ab-wizard__pill ab-wizard__pill--accent">Custom scale</span>
                        ) : null}
                      </button>
                    ))
                  : null}
              </React.Fragment>
            ))}
            </div>
          </aside>
          <div className="ab-wizard__editor">
            {!editTarget || !unit ? (
              <p className="ab-wizard__hint">
                Select a capability (or a sub-capability when listed) to edit content.
              </p>
            ) : (
              <>
                <p className="ab-wizard__eyebrow" style={{ textTransform: "none", letterSpacing: 0, fontSize: "0.8125rem", fontWeight: 400 }}>
                  {blueprint.sourceTraxccelFrameworkTitle}
                </p>
                <div className="ab-wizard__unit-head">
                  <h2 className="ab-wizard__unit-title">{blueprintUnitName(unit)}</h2>
                  <span className="ab-wizard__pill">
                    <Lock size={12} aria-hidden />
                    Capability locked
                  </span>
                </div>
                <div className="ab-wizard__tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={unitTab === "questions"}
                    className={
                      unitTab === "questions"
                        ? "ab-wizard__tab ab-wizard__tab--active"
                        : "ab-wizard__tab"
                    }
                    onClick={() => setUnitTab("questions")}
                  >
                    Questions <span className="ab-wizard__hint">{questionTotal}</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={unitTab === "maturity"}
                    className={
                      unitTab === "maturity"
                        ? "ab-wizard__tab ab-wizard__tab--active"
                        : "ab-wizard__tab"
                    }
                    onClick={() => setUnitTab("maturity")}
                  >
                    Maturity scale
                    <span
                      className={
                        unitHasCustomScale(unit)
                          ? "ab-wizard__pill ab-wizard__pill--accent"
                          : "ab-wizard__pill"
                      }
                    >
                      {unitHasCustomScale(unit) ? "Custom" : "Default"}
                    </span>
                  </button>
                </div>

                {unitTab === "questions" ? (
                  <>
                    <p className="ab-wizard__hint" style={{ marginBottom: "0.85rem" }}>
                      Current-state questions. Edits apply only to this assessment.
                    </p>
                    <div className="ab-wizard__question-group">
                      <SubCapabilityQuestionListEditor
                        label="Current state questions"
                        stateKind={QUESTION_STATE.current}
                        questions={toEditorQuestions(unit.currentStateQuestions)}
                        disabled={isReadOnly}
                        numbered
                        autoGrow
                        onChange={(next) => updateUnitQuestions(QUESTION_STATE.current, next)}
                      />
                    </div>
                    <div className="ab-wizard__question-group">
                      <p className="ab-wizard__hint" style={{ marginBottom: "0.85rem" }}>
                        Future-state questions.
                      </p>
                      <SubCapabilityQuestionListEditor
                        label="Future state questions"
                        stateKind={QUESTION_STATE.future}
                        questions={toEditorQuestions(unit.futureStateQuestions)}
                        disabled={isReadOnly}
                        numbered
                        autoGrow
                        onChange={(next) => updateUnitQuestions(QUESTION_STATE.future, next)}
                      />
                    </div>
                  </>
                ) : (
                  renderMaturityTab()
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    if (currentStep === "company") {
      return (
        <div className="ab-wizard__split">
          <div className="ab-wizard__stack">
            <div>
              <h2 className="ab-wizard__step-title">Which company is this for?</h2>
              <p className="ab-wizard__step-desc">The company being assessed.</p>
            </div>
            <SearchSelect
              id="ab-wizard-company"
              label="Company"
              placeholder="Search companies…"
              disabled={isReadOnly}
              options={companyOptions}
              selectedId={blueprint.companyId ?? null}
              selectedName={companyName}
              onSelect={(id) => updateBlueprint({ companyId: id, projectId: null })}
            />
          </div>
          {renderSoFar()}
        </div>
      );
    }

    if (currentStep === "project") {
      return (
        <div className="ab-wizard__split">
          <div className="ab-wizard__stack">
            <div>
              <h2 className="ab-wizard__step-title">Which project will use it?</h2>
              <p className="ab-wizard__step-desc">
                When published, everyone on the project works from this assessment.
              </p>
            </div>
            {!blueprint.companyId ? (
              <Alert variant="warning" description="Select a company first." />
            ) : (
              <SearchSelect
                id="ab-wizard-project"
                label="Project"
                hint={companyName ? `Projects for ${companyName}` : undefined}
                placeholder="Search projects…"
                disabled={isReadOnly}
                options={projectOptions}
                selectedId={blueprint.projectId ?? null}
                selectedName={projectName}
                onSelect={(id) => updateBlueprint({ projectId: id })}
              />
            )}
            <div className="ab-wizard__note">
              <Info size={16} aria-hidden />
              <span>Optional. You can save without a project and link one before publishing.</span>
            </div>
          </div>
          {renderSoFar()}
        </div>
      );
    }

    const questionCount = countIncludedQuestions(blueprint);
    const reviewCard = (
      label: string,
      step: number,
      body: React.ReactNode,
      full = false
    ) => (
      <div className={full ? "ab-wizard__review-card ab-wizard__review-card--full" : "ab-wizard__review-card"}>
        <div className="ab-wizard__review-card-head">
          <span className="ab-wizard__eyebrow">{label}</span>
          {!isReadOnly ? (
            <button type="button" className="ab-wizard__review-edit" onClick={() => setStepIndex(step)}>
              Edit
            </button>
          ) : null}
        </div>
        {body}
      </div>
    );

    return (
      <div className="ab-wizard__pad ab-wizard__stack">
        <div className="ab-wizard__review-head">
          <h2 className="ab-wizard__step-title" style={{ margin: 0 }}>
            Check everything before publishing
          </h2>
          <Badge
            variant={
              blueprint.status === "Published"
                ? "success"
                : blueprint.status === "Draft"
                  ? "warning"
                  : "default"
            }
          >
            {blueprint.status}
          </Badge>
        </div>
        <div className="ab-wizard__review-grid">
          {reviewCard(
            "Details",
            0,
            <>
              <p className="ab-wizard__review-value">{blueprint.title}</p>
              <p className="ab-wizard__review-sub">{blueprint.description || "No description"}</p>
            </>,
            true
          )}
          {reviewCard(
            "Framework",
            1,
            <>
              <p className="ab-wizard__review-value">{blueprint.sourceTraxccelFrameworkTitle}</p>
            </>
          )}
          {reviewCard(
            "Questions",
            1,
            <p className="ab-wizard__review-value">
              <span className="ab-wizard__review-big">{questionCount}</span>{" "}
              <span className="ab-wizard__review-sub">
                across {blueprint.capabilities.length}{" "}
                {blueprint.capabilities.length === 1 ? "capability" : "capabilities"}
              </span>
            </p>
          )}
          {reviewCard("Company", 2, <p className="ab-wizard__review-value">{companyName || "—"}</p>)}
          {reviewCard("Project", 3, <p className="ab-wizard__review-value">{projectName || "—"}</p>)}
        </div>
        {!isReadOnly && (
          <div className="ab-wizard__note ab-wizard__note--info">
            <Info size={16} aria-hidden />
            <span>
              {projectName ? (
                <>
                  Publishing links this assessment to <strong>{projectName}</strong>. Everyone on the
                  project will answer these {questionCount} questions.
                </>
              ) : (
                "Select a company and project before publishing."
              )}
            </span>
          </div>
        )}
      </div>
    );
  };

  const nextLabels: Record<string, string> = {
    framework: "Continue to questions",
    questions: "Continue to company",
    company: "Continue to project",
    project: "Review",
  };

  return (
    <div className="ab-wizard">
      <div className="ab-wizard__steps" role="tablist" aria-label="Assessment steps">
        {WIZARD_STEPS.map((step, index) => {
          const done = index < stepIndex;
          const active = index === stepIndex;
          return (
            <React.Fragment key={step.key}>
              {index > 0 ? (
                <span
                  className={
                    index <= stepIndex
                      ? "ab-wizard__step-line ab-wizard__step-line--done"
                      : "ab-wizard__step-line"
                  }
                  aria-hidden
                />
              ) : null}
              <button
                type="button"
                role="tab"
                aria-selected={active}
                aria-current={active ? "step" : undefined}
                className={
                  active
                    ? "ab-wizard__step ab-wizard__step--active"
                    : done
                      ? "ab-wizard__step ab-wizard__step--done"
                      : "ab-wizard__step"
                }
                disabled={!blueprint && index > 0}
                onClick={() => blueprint && setStepIndex(index)}
              >
                <span className="ab-wizard__step-dot">
                  {done ? <Check size={14} aria-hidden /> : index + 1}
                </span>
                {step.label}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div
        className={
          currentStep === "framework" && !blueprint && !loading
            ? "ab-wizard__content ab-wizard__content--fixed"
            : "ab-wizard__content"
        }
      >
        {renderStepContent()}
      </div>

      <div className="ab-wizard__footer">
        <Button
          variant="ghost"
          disabled={stepIndex === 0 || saving}
          onClick={() => setStepIndex((prev) => Math.max(0, prev - 1))}
        >
          <ChevronLeft size={16} aria-hidden />
          Back
        </Button>
        <div className="ab-wizard__footer-right">
          {currentStep === "publish" ? (
            isReadOnly ? (
              <Button variant="primary" onClick={onCompleted}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  disabled={saving}
                  onClick={async () => {
                    const saved = await persistDraft();
                    if (saved) {
                      dashboardToast.success("Draft saved.");
                      onCompleted();
                    }
                  }}
                >
                  Save as draft
                </Button>
                <Button
                  variant="primary"
                  disabled={!blueprint?.companyId || !blueprint?.projectId}
                  isLoading={saving}
                  onClick={handlePublish}
                >
                  Publish assessment
                </Button>
              </>
            )
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={saving}
                disabled={
                  currentStep === "framework" &&
                  !blueprint &&
                  (!selectedFrameworkId || !draftTitle.trim())
                }
                onClick={handleNext}
              >
                {nextLabels[currentStep] ?? "Continue"}
                <ChevronRight size={16} aria-hidden />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentBlueprintWizardPanel;
